// src/components/pwa/PWAInstallPrompt.tsx
// Muestra un banner de instalación cuando el navegador soporta PWA

import { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function PWAInstallPrompt() {
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Detectar si ya está instalada
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Escuchar el evento del navegador que indica que la app es instalable
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallEvent(e as BeforeInstallPromptEvent);

      // Solo mostrar si el usuario no lo descartó antes (en esta sesión)
      const dismissed = sessionStorage.getItem('pwa-prompt-dismissed');
      if (!dismissed) {
        setIsVisible(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Detectar si se instaló
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setIsVisible(false);
    });

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installEvent) return;
    await installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    if (outcome === 'accepted') {
      setIsVisible(false);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    sessionStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  if (!isVisible || isInstalled) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '24px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '14px 20px',
        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
        border: '1px solid rgba(99, 102, 241, 0.4)',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(99,102,241,0.1)',
        color: '#f1f5f9',
        fontSize: '14px',
        whiteSpace: 'nowrap',
        animation: 'slideUp 0.3s ease',
        backdropFilter: 'blur(8px)',
      }}
    >
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateX(-50%) translateY(16px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>

      <img
        src="/icon-192x192.png"
        alt="SGC"
        style={{ width: 36, height: 36, borderRadius: 8 }}
      />

      <div>
        <p style={{ margin: 0, fontWeight: 600, color: '#f1f5f9' }}>
          Instalar SGC
        </p>
        <p style={{ margin: 0, fontSize: 12, color: '#94a3b8' }}>
          Accede más rápido desde tu escritorio
        </p>
      </div>

      <button
        onClick={handleInstall}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 16px',
          background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
          border: 'none',
          borderRadius: 10,
          color: '#fff',
          fontWeight: 600,
          fontSize: 13,
          cursor: 'pointer',
          transition: 'opacity 0.2s',
        }}
        onMouseOver={e => (e.currentTarget.style.opacity = '0.85')}
        onMouseOut={e => (e.currentTarget.style.opacity = '1')}
      >
        <Download size={15} />
        Instalar
      </button>

      <button
        onClick={handleDismiss}
        style={{
          background: 'none',
          border: 'none',
          color: '#64748b',
          cursor: 'pointer',
          padding: 4,
          display: 'flex',
          alignItems: 'center',
          borderRadius: 6,
          transition: 'color 0.2s',
        }}
        onMouseOver={e => (e.currentTarget.style.color = '#94a3b8')}
        onMouseOut={e => (e.currentTarget.style.color = '#64748b')}
        aria-label="Cerrar"
      >
        <X size={16} />
      </button>
    </div>
  );
}
